import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router';
import { useCallback, useMemo } from 'react';
import { useFetchReceivableInvoice } from 'receivable-invoices-js/react/api/queries/fetch-receivable-invoice/fetch-receivable-invoice';
import { paymentLinkQueries } from 'receivable-invoices-js/react/api/queries/fetch-payment-link/fetch-payment-link';
import { useFetchReceivableInvoicesUpload } from 'receivable-invoices-js/react/api/queries/fetch-receivable-invoices-upload/fetch-receivable-invoices-upload';
import { useFetchClientPortalSettings } from 'receivable-invoices-js/react/api/queries/fetch-client-portal-settings';
import { fetchPdfSrc } from 'receivable-invoices-js/react/utils/pdf-utils/pdf-utils';
import type { Attachment } from 'receivable-invoices-js/react/components/shared/responsive-view/types';
import { useStatusPolling } from './hooks/use-status-polling';

export function useRouteQuery() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const finalized = searchParams.get('finalized');
  const queryClient = useQueryClient();

  // Fetch invoice
  const invoiceId = id || '';
  const {
    data: invoice,
    isLoading: invoiceLoading,
    isError: invoiceError,
  } = useFetchReceivableInvoice({ id: invoiceId }, {}, { enabled: Boolean(invoiceId) });

  // Fetch PDF source separately
  const { data: pdfSrc, isLoading: pdfLoading } = useQuery({
    queryKey: ['invoice-pdf-src', invoiceId, invoice?.pdfUrl],
    queryFn: () => fetchPdfSrc(invoice?.pdfUrl || ''),
    enabled: Boolean(invoice?.pdfUrl),
  });

  // Fetch payment link
  const {
    data: paymentLink,
    isLoading: paymentLinkLoading,
    error: paymentLinkError,
  } = useQuery({
    ...paymentLinkQueries.findItem({ resourceId: invoiceId }),
    enabled: Boolean(invoiceId) && Boolean(invoice?.paymentLink?.methods),
  });

  // Fetch client portal settings
  const clientId = invoice?.customerSnapshot.id || '';
  const {
    data: clientPortalSettings,
    isLoading: clientPortalSettingsLoading,
    isError: clientPortalSettingsError,
  } = useFetchClientPortalSettings({ clientId }, {}, { enabled: Boolean(clientId) });

  const isClientPortalEnabled = clientPortalSettings?.clientPortalEnabled ?? false;

  // Get attachment IDs from relationships
  const receivableInvoicingUploadId = invoice?.receivableInvoicingUploadId || '';
  const termsConditionsUploadId = invoice?.termsConditionsUploadId || '';

  // Fetch attachments
  const {
    data: receivableInvoicingUploadQuery,
    isLoading: receivableInvoicingUploadLoading,
    isError: receivableInvoicingUploadError,
  } = useFetchReceivableInvoicesUpload(
    receivableInvoicingUploadId,
    {},
    { enabled: Boolean(receivableInvoicingUploadId) }
  );

  const {
    data: termsConditionsUploadQuery,
    isLoading: termsConditionsUploadLoading,
    isError: termsConditionsUploadError,
  } = useFetchReceivableInvoicesUpload(
    termsConditionsUploadId,
    {},
    { enabled: Boolean(termsConditionsUploadId) }
  );

  // Process attachments
  const getAttachments = useCallback((): Attachment[] => {
    const attachments: Attachment[] = [];

    if (receivableInvoicingUploadQuery) {
      attachments.push({
        id: 'attachment' as const,
        url: receivableInvoicingUploadQuery.url,
        filename: receivableInvoicingUploadQuery.filename,
        ...(typeof receivableInvoicingUploadQuery.src === 'string' && {
          src: receivableInvoicingUploadQuery.src,
        }),
      });
    }

    if (termsConditionsUploadQuery) {
      attachments.push({
        id: 'terms-conditions' as const,
        url: termsConditionsUploadQuery.url,
        filename: termsConditionsUploadQuery.filename,
        ...(typeof termsConditionsUploadQuery.src === 'string' && {
          src: termsConditionsUploadQuery.src,
        }),
      });
    }

    return attachments;
  }, [receivableInvoicingUploadQuery, termsConditionsUploadQuery]);

  const paymentPollingStatus = useStatusPolling({
    queryClient,
    invoiceId,
    enabled: finalized === 'true' && Boolean(paymentLink),
  });

  const isLoading = useMemo(() => {
    return (
      invoiceLoading ||
      pdfLoading ||
      paymentLinkLoading ||
      clientPortalSettingsLoading ||
      receivableInvoicingUploadLoading ||
      termsConditionsUploadLoading
    );
  }, [
    invoiceLoading,
    pdfLoading,
    paymentLinkLoading,
    clientPortalSettingsLoading,
    receivableInvoicingUploadLoading,
    termsConditionsUploadLoading,
  ]);

  const isError = useMemo(() => {
    return (
      invoiceError ||
      (paymentLinkError && !('status' in paymentLinkError && paymentLinkError.status === 404)) ||
      clientPortalSettingsError ||
      receivableInvoicingUploadError ||
      termsConditionsUploadError
    );
  }, [
    invoiceError,
    paymentLinkError,
    clientPortalSettingsError,
    receivableInvoicingUploadError,
    termsConditionsUploadError,
  ]);

  return {
    invoice: invoice ? { ...invoice, pdfSrc } : undefined,
    paymentLink: paymentLink && paymentLink.status !== 'canceled' ? paymentLink : null,
    attachments: getAttachments(),
    isClientPortalEnabled,
    isLoading,
    isError,
    paymentPollingStatus,
  };
}
